import * as React from "react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card";

const OrdinaryPageBody = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("min-w-0 space-y-5", className)} {...props} />
));
OrdinaryPageBody.displayName = "OrdinaryPageBody";

function OrdinaryPageHeader({ title, actions, className, ...props }) {
  return (
    <header
      className={cn("flex min-w-0 flex-wrap items-start justify-between gap-4", className)}
      {...props}
    >
      <div className="min-w-0">
        <h1 className="break-words text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div> : null}
    </header>
  );
}

function OrdinarySection({ title, description, actions, children, className, contentClassName, ...props }) {
  const hasHeader = title || description || actions;
  return (
    <Card className={cn("min-w-0 overflow-hidden rounded-sm", className)} {...props}>
      {hasHeader ? (
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 border-b border-border p-5">
          <div className="min-w-0">
            {title ? <CardTitle className="break-words text-base">{title}</CardTitle> : null}
            {description ? <CardDescription className="mt-1 break-words">{description}</CardDescription> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div> : null}
        </CardHeader>
      ) : null}
      <CardContent className={cn("p-5", contentClassName)}>{children}</CardContent>
    </Card>
  );
}

function PersistentPageHeader({
  context,
  mode,
  title,
  metadata,
  details,
  primaryActions,
  secondaryActions,
  className,
  contentClassName,
  contentTestId,
  topRowTestId,
  identityTestId,
  primaryActionRowTestId,
  secondaryActionRowTestId,
  ...props
}) {
  return (
    <header className={cn("sticky top-0 z-40 border-b bg-card w-full border-border text-card-foreground", className)} {...props}>
      <div className={cn("mx-auto max-w-[1504px] px-4 py-4 sm:px-6 xl:px-8", contentClassName)} data-testid={contentTestId}>
      <div className="flex flex-wrap items-stretch justify-between gap-4" data-testid={topRowTestId}>
        <div className="min-w-0 flex-1" data-testid={identityTestId}>
          <div className="flex flex-wrap items-center gap-2">
            {context}
            {mode ? <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{mode}</span> : null}
          </div>
          <h1 className="mt-1 min-w-0 break-words text-xl font-semibold" title={typeof title === "string" ? title : undefined}>{title}</h1>
          {metadata ? <p className="mt-1 break-words text-xs text-muted-foreground">{metadata}</p> : null}
          {details}
        </div>
        <div className="grid min-w-0 grid-rows-[minmax(2rem,auto)_1fr_minmax(2.5rem,auto)] items-start justify-items-end self-stretch" data-testid="persistent-header-actions">
          <div className="flex min-h-8 flex-wrap items-center justify-end gap-2" data-testid={primaryActionRowTestId || "persistent-header-action-row-1"}>
            {primaryActions}
          </div>
          <div className="row-start-3 flex min-h-10 flex-wrap items-center justify-end gap-2" data-testid={secondaryActionRowTestId || "persistent-header-action-row-2"}>
            {secondaryActions}
          </div>
        </div>
      </div>
      </div>
    </header>
  );
}

const PersistentHeaderUtilityAction = React.forwardRef(({ className, ...props }, ref) => (
  <Button
    ref={ref}
    variant="link"
    className={cn("h-8 min-h-8 rounded-none border-0 bg-transparent px-2.5 pb-2.5 pt-0.5 text-sm text-[var(--neon-cyan-outline-foreground)] no-underline shadow-none hover:bg-transparent hover:text-[var(--neon-cyan-base)] hover:no-underline hover:shadow-none active:bg-transparent active:opacity-80 focus-visible:ring-[var(--neon-cyan-base)] focus-visible:ring-offset-1 focus-visible:shadow-none", className)}
    {...props}
  />
));
PersistentHeaderUtilityAction.displayName = "PersistentHeaderUtilityAction";

function PersistentPageLoading({ label, bodyClassName, ...props }) {
  return (
    <>
      <PersistentPageHeader title={label} aria-busy="true" {...props} />
      <div className={cn("mx-auto max-w-[1504px] px-4 py-6 sm:px-6 xl:px-8", bodyClassName)}>
        <p className="text-sm text-muted-foreground" role="status">{label}</p>
      </div>
    </>
  );
}

export {
  OrdinaryPageBody,
  OrdinaryPageHeader,
  OrdinarySection,
  PersistentHeaderUtilityAction,
  PersistentPageHeader,
  PersistentPageLoading,
};
